import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  MdAdd,
  MdAutoAwesome,
  MdBookmark,
  MdBookmarkBorder,
  MdExpandMore,
  MdFavorite,
  MdRefresh,
} from "react-icons/md";
import Card from "components/card";
import Button from "components/button";
import AlertPane from "components/alert";
import Dialog from "components/dialog";
import { Checkbox, InputField, Switch } from "components/fields";
import ChoiceCard from "components/heartbeat/ChoiceCard";
import OptionGroup from "components/heartbeat/OptionGroup";
import QuestionCard from "./QuestionCard";

const CAMPAIGN_TYPES = [
  { value: "short", label: "Short (3-5 questions)", description: "Quick check-in for time-sensitive moments" },
  { value: "standard", label: "Standard (5-8 questions)", description: "Balanced assessment for regular use" },
  { value: "in_depth", label: "In-Depth (8-12 questions)", description: "Comprehensive spiritual health survey" },
];

const FOCUS_AREAS = [
  { value: "faith_journey", label: "Faith Journey", description: "Personal spiritual growth and relationship with God" },
  { value: "community", label: "Community", description: "Connection with church and small groups" },
  { value: "life_challenges", label: "Life & Wellbeing", description: "Life circumstances and personal challenges" },
];

const SURVEY_GOALS = [
  { value: "gather_feedback", label: "Gather feedback" },
  { value: "measure_satisfaction", label: "Measure satisfaction" },
  { value: "understand_preferences", label: "Understand preferences" },
  { value: "identify_improvements", label: "Identify improvements" },
  { value: "assess_engagement", label: "Assess engagement" },
  { value: "collect_suggestions", label: "Collect suggestions" },
];

const TemplateMenu = ({ templates, onSelect }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const handlePointer = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handlePointer);
    return () => document.removeEventListener("mousedown", handlePointer);
  }, [open]);

  const handleKeyDown = (event) => {
    if (event.key === "Escape" && open) {
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    }
  };

  return (
    <div ref={rootRef} className="relative" onKeyDown={handleKeyDown}>
      <Button
        ref={triggerRef}
        variant="secondary"
        leftIcon={<MdBookmarkBorder aria-hidden="true" className="h-5 w-5" />}
        rightIcon={
          <MdExpandMore
            aria-hidden="true"
            className={`h-5 w-5 transition-transform ${open ? "rotate-180" : ""}`}
          />
        }
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen(!open)}
      >
        Load Template
      </Button>
      {open && (
        <div
          id={menuId}
          className="absolute left-0 top-full z-20 mt-2 w-64 rounded-control border border-line bg-surface shadow-overlay"
        >
          {templates.length > 0 ? (
            <ul className="max-h-60 overflow-y-auto py-2">
              {templates.map((template) => (
                <li key={template.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      onSelect(template);
                    }}
                    className="flex w-full items-start gap-2 px-4 py-2 text-left transition-colors hover:bg-surface-subtle focus:outline-none focus-visible:bg-surface-subtle"
                  >
                    <MdBookmark aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-primary dark:text-accent" />
                    <span>
                      <span className="block text-sm font-medium text-fg">{template.name}</span>
                      <span className="block text-xs text-fg-muted">
                        {template.questions?.length || 0} questions
                        {template.isAiGenerated && " • AI Generated"}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="px-4 py-3 text-center text-sm text-fg-muted">No saved templates yet</p>
          )}
        </div>
      )}
    </div>
  );
};

export const SaveTemplateDialog = ({ editor }) => {
  const {
    formData,
    showSaveTemplateModal,
    closeSaveTemplateModal,
    templateName,
    setTemplateName,
    savingTemplate,
    handleSaveTemplate,
  } = editor;
  const nameRef = useRef(null);

  return (
    <Dialog
      isOpen={showSaveTemplateModal}
      onClose={closeSaveTemplateModal}
      title="Save as Template"
      description={`Save these ${formData.questions.length} questions as a template to reuse in other campaigns.`}
      initialFocusRef={nameRef}
      footer={
        <>
          <Button variant="secondary" onClick={closeSaveTemplateModal}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="save-template-form"
            loading={savingTemplate}
            disabled={!templateName.trim()}
            leftIcon={<MdBookmark aria-hidden="true" className="h-5 w-5" />}
          >
            {savingTemplate ? "Saving…" : "Save Template"}
          </Button>
        </>
      }
    >
      <form
        id="save-template-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSaveTemplate();
        }}
      >
        <InputField
          ref={nameRef}
          label="Template Name"
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          placeholder="e.g., Visitor Welcome Survey"
        />
      </form>
    </Dialog>
  );
};

const QuestionsSection = ({ editor }) => {
  const {
    isEdit,
    formData,
    errors,
    handleChange,
    responseCount,
    templates,
    loadTemplate,
    loadSliderQuestions,
    setShowSaveTemplateModal,
    showAiGenerator,
    setShowAiGenerator,
    aiCampaignType,
    setAiCampaignType,
    aiFocusAreas,
    toggleFocusArea,
    generatingAi,
    handleGenerateAiQuestions,
    addQuestion,
  } = editor;
  const aiPanelId = useId();
  const prayerTitleId = useId();
  const prayerDescriptionId = useId();

  return (
    <div className="flex flex-col gap-5">
      {isEdit && responseCount > 0 && (
        <AlertPane
          tone="warning"
          className=""
          title={`This campaign has ${responseCount} response${responseCount !== 1 ? "s" : ""}`}
        >
          <p>
            Modifying questions may cause existing response data to display incorrectly. If you
            need to make significant changes, consider creating a new campaign instead and
            deactivating this one.
          </p>
          <Button as={Link} to="/admin/heartbeat/new" variant="secondary" size="sm" className="mt-3">
            Create New Campaign
          </Button>
        </AlertPane>
      )}

      <Card padding="md" className="gap-4">
        <OptionGroup label="Survey Type">
          <div className="grid gap-4 md:grid-cols-2">
            <ChoiceCard
              selected={formData.surveyType === "spiritual"}
              onClick={() => handleChange("surveyType", "spiritual")}
              title="Spiritual Health Survey"
              description="Questions focused on faith journey, community connection, and life challenges"
            />
            <ChoiceCard
              selected={formData.surveyType === "general"}
              onClick={() => handleChange("surveyType", "general")}
              title="General Survey"
              description="Custom topic survey for feedback, events, volunteer satisfaction, etc."
            />
          </div>
        </OptionGroup>

        {formData.surveyType === "general" && (
          <div className="space-y-4 rounded-control bg-surface-raised p-4">
            <InputField
              label="Survey Topic"
              value={formData.surveyTopic}
              onChange={(e) => handleChange("surveyTopic", e.target.value)}
              placeholder="e.g., Volunteer satisfaction, Event feedback, Ministry interest"
              hint="This will guide the AI when generating questions"
            />
            <OptionGroup label="Survey Goals" hint="Select all that apply">
              <div className="grid gap-3 md:grid-cols-2">
                {SURVEY_GOALS.map((goal) => (
                  <Checkbox
                    key={goal.value}
                    label={goal.label}
                    checked={formData.surveyGoals.includes(goal.value)}
                    onChange={(e) => {
                      const newGoals = e.target.checked
                        ? [...formData.surveyGoals, goal.value]
                        : formData.surveyGoals.filter((g) => g !== goal.value);
                      handleChange("surveyGoals", newGoals);
                    }}
                  />
                ))}
              </div>
            </OptionGroup>
          </div>
        )}
      </Card>

      <div className="flex flex-wrap items-center gap-3">
        <TemplateMenu templates={templates} onSelect={loadTemplate} />
        <Button
          variant="secondary"
          leftIcon={<MdRefresh aria-hidden="true" className="h-5 w-5" />}
          onClick={loadSliderQuestions}
        >
          Default Sliders
        </Button>
        {formData.questions.length > 0 && (
          <Button
            variant="secondary"
            leftIcon={<MdBookmark aria-hidden="true" className="h-5 w-5" />}
            onClick={() => setShowSaveTemplateModal(true)}
          >
            Save as Template
          </Button>
        )}
      </div>

      <Card>
        <button
          type="button"
          aria-expanded={showAiGenerator}
          aria-controls={aiPanelId}
          onClick={() => setShowAiGenerator(!showAiGenerator)}
          className="flex w-full items-center justify-between gap-3 rounded-card p-4 text-left focus-ring"
        >
          <span className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-control bg-brand-100 dark:bg-brand-900/30">
              <MdAutoAwesome aria-hidden="true" className="h-5 w-5 text-primary dark:text-accent" />
            </span>
            <span>
              <span className="block font-semibold text-fg">AI Question Generator</span>
              <span className="block text-sm text-fg-muted">
                Let AI create questions tailored to your campaign
              </span>
            </span>
          </span>
          <MdExpandMore
            aria-hidden="true"
            className={`h-6 w-6 shrink-0 text-fg-muted transition-transform ${showAiGenerator ? "rotate-180" : ""}`}
          />
        </button>

        {showAiGenerator && (
          <div id={aiPanelId} className="space-y-4 border-t border-line p-4">
            <OptionGroup label="Survey Length">
              <div className="grid gap-3 md:grid-cols-3">
                {CAMPAIGN_TYPES.map((type) => (
                  <ChoiceCard
                    key={type.value}
                    selected={aiCampaignType === type.value}
                    onClick={() => setAiCampaignType(type.value)}
                    title={type.label}
                    description={type.description}
                  />
                ))}
              </div>
            </OptionGroup>

            {formData.surveyType === "spiritual" && (
              <OptionGroup label="Focus Areas (select at least one)">
                <div className="grid gap-3 md:grid-cols-3">
                  {FOCUS_AREAS.map((area) => (
                    <ChoiceCard
                      key={area.value}
                      multiple
                      selected={aiFocusAreas.includes(area.value)}
                      onClick={() => toggleFocusArea(area.value)}
                      title={area.label}
                      description={area.description}
                    />
                  ))}
                </div>
              </OptionGroup>
            )}

            <div className="flex flex-wrap items-center justify-end gap-3 pt-2">
              {formData.questions.length > 0 && (
                <p className="text-sm text-warning-fg">
                  Note: This will replace your current {formData.questions.length} question(s)
                </p>
              )}
              <Button
                onClick={handleGenerateAiQuestions}
                loading={generatingAi}
                disabled={formData.surveyType === "spiritual" && aiFocusAreas.length === 0}
                leftIcon={<MdAutoAwesome aria-hidden="true" className="h-5 w-5" />}
              >
                {generatingAi ? "Generating…" : "Generate Questions"}
              </Button>
            </div>
          </div>
        )}
      </Card>

      <Card padding="sm">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-control bg-brand-100 dark:bg-brand-900/30">
              <MdFavorite aria-hidden="true" className="h-5 w-5 text-primary dark:text-accent" />
            </span>
            <div>
              <h3 id={prayerTitleId} className="font-semibold text-fg">
                Prayer Requests
              </h3>
              <p id={prayerDescriptionId} className="text-sm text-fg-muted">
                Allow respondents to submit a prayer request after completing the survey
              </p>
            </div>
          </div>
          <Switch
            aria-labelledby={prayerTitleId}
            aria-describedby={prayerDescriptionId}
            checked={formData.enablePrayerRequests}
            onChange={(e) => handleChange("enablePrayerRequests", e.target.checked)}
          />
        </div>
      </Card>

      {formData.questions.map((question, index) => (
        <QuestionCard key={question.id} editor={editor} question={question} index={index} />
      ))}

      {errors.questions && (
        <p role="alert" className="text-sm text-danger-fg">
          {errors.questions}
        </p>
      )}

      <Button
        variant="secondary"
        size="lg"
        fullWidth
        className="border-dashed"
        leftIcon={<MdAdd aria-hidden="true" className="h-5 w-5" />}
        onClick={addQuestion}
        data-invalid-focus={errors.questions ? "true" : undefined}
      >
        Add Question
      </Button>
    </div>
  );
};

export default QuestionsSection;
